import { Injectable } from '@angular/core';
import { AppTableColumn, ColumnPreference } from '../_models/models';
import { LocalStorageService } from './local-storage.service';
import { isCustomizableColumn } from '../_common/app-table-column.helper';

@Injectable({ providedIn: 'root' })
export class ColumnPreferencesService {
  constructor(private localStorage: LocalStorageService) {}

  read(storageKey: string): ColumnPreference[] {
    return this.localStorage.readArray<ColumnPreference>(storageKey);
  }

  hasStoredWidths(storageKey: string): boolean {
    return this.read(storageKey).some(pref => !!pref.width);
  }

  applyToColumns(columns: AppTableColumn[], storageKey: string): AppTableColumn[] {
    if (!columns || !storageKey) {
      return columns;
    }
    const prefsById = new Map(this.read(storageKey).map(pref => [pref.id, pref]));
    if (prefsById.size === 0) {
      return columns;
    }
    return columns.map(col => this.mergePreference(col, prefsById.get(col.id)));
  }

  persist(storageKey: string, columns: AppTableColumn[]): void {
    this.mergeInto(storageKey, columns, col => !!col.columnGroup, col => ({ isHidden: !!col.isHidden }));
  }

  persistColumnWidths(storageKey: string, columns: AppTableColumn[]): void {
    this.mergeInto(
      storageKey,
      columns,
      col => isCustomizableColumn(col) && !col.isHidden && !!col.colWidth,
      col => ({ width: col.colWidth })
    );
  }

  clearColumnWidth(storageKey: string, columnId: string | number): void {
    if (!storageKey) {
      return;
    }
    const mergedById = this.readAsMap(storageKey);
    const preference = mergedById.get(columnId);
    if (!preference?.width) {
      return;
    }
    delete preference.width;
    if (preference.isHidden === undefined) {
      mergedById.delete(columnId);
    }
    this.localStorage.write(storageKey, [...mergedById.values()]);
  }

  /** Patches the stored preference of every column that passes the filter, keeping whatever else is stored for it. */
  private mergeInto(
    storageKey: string,
    columns: AppTableColumn[],
    filter: (column: AppTableColumn) => boolean,
    patch: (column: AppTableColumn) => Partial<ColumnPreference>
  ): void {
    if (!storageKey) {
      return;
    }
    const mergedById = this.readAsMap(storageKey);
    columns
      .filter(filter)
      .forEach(col => mergedById.set(col.id, { ...mergedById.get(col.id), id: col.id, ...patch(col) }));
    this.localStorage.write(storageKey, [...mergedById.values()]);
  }

  private readAsMap(storageKey: string): Map<string | number, ColumnPreference> {
    return new Map<string | number, ColumnPreference>(this.read(storageKey).map(pref => [pref.id, pref]));
  }

  private mergePreference(column: AppTableColumn, preference: ColumnPreference): AppTableColumn {
    if (!preference) {
      return column;
    }
    const merged = { ...column };
    if (preference.isHidden !== undefined) {
      merged.isHidden = preference.isHidden;
    }
    if (preference.width) {
      merged.colWidth = preference.width;
      merged.maxWidth = undefined;
    }
    return merged;
  }
}
